/**
 * Orik as a harness client plugin: what Orik's own components read from the booted kernel.
 *
 * The conversation panel and the conversation list are the harness's own (`ui-conversation` in Orik's frame,
 * `ui-workspace` in Orik's sidebar), and they read the session plane directly. Orik's components around
 * them — the header, the artifact panel, the model picker, the browser hand-off — need a few facts from the
 * same kernel: the host's catalogue, which session is on screen, and the delivery receipts in the open
 * session's window. This plugin publishes them as a bridge on the global object, and forwards the navigation
 * Orik starts (a deep link, "New conversation") to `ui-workspace`, which owns it.
 *
 * The plugin is an Orik artifact installed by our own boot entry (`harness/in-app.ts`) with the
 * context it just booted, rather than registered as a synthetic manifest entry. That is a
 * deliberate departure: the kernel's entry reconciliation reports a failed entry as
 * "cannot resolve entry <random id>" — it deletes the entry and then resolves the id it minted,
 * so the cause is swallowed — and this plugin has no lifecycle the kernel has to own. Installed
 * directly, a failure here is a failure in this file's own stack.
 */
import { deliveryFrom, type SessionWindowEntry } from "../src/lib/chat/session-source";

/** The plugin's name, which is what the kernel logs it as. */
export const ORIK_CLIENT_ID = "atlas-client";

/** Bound a wait for the native connection baseline; never poll the host. */
const CATALOGUE_WAIT_MS = 20_000;

/** Cross-bundle notification that the native bridge is now published. */
export const CATALOGUE_BRIDGE_EVENT = "atlas:dsh-catalogue-ready";

/**
 * DSH's own label for the conversation on screen (`ui-session` declares it, `ui-workspace` holds it).
 *
 * A row's `retainedBy.mainView` is how the native catalogue marks the current session: `ui-workspace` retains
 * exactly one session under it, and releases the previous one when it switches.
 */
const MAIN_VIEW_SOURCE = "mainView";

/** The event window a binding exposes: a contiguous, append-only view of the log. */
interface EventWindow {
  readonly entries: readonly SessionWindowEntry[];
  readonly revision?: number;
}

/** The snapshot half of the harness's observable. */
export interface Observable<T> {
  getSnapshot(): T;
  subscribe(listener: () => void): () => void;
}

/** One workspace row, as the registry's client store projects it. */
interface OrikWorkspace {
  readonly workspaceId: string;
  readonly path: string;
  readonly title: string;
  readonly sessionIds?: readonly string[];
}

/** The workspace registry's client store. */
interface Workspaces {
  /** Host-authoritative rows, order and archive set. */
  list?: Observable<{
    readonly phase?: "pending" | "ready";
    /** The workspace accounts, each carrying the sessions it accounts for in its manual order. */
    readonly items?: readonly OrikWorkspace[];
    readonly archivedSessionIds?: readonly string[];
  }>;
}

/** `ui-workspace`'s navigation, which owns the conversation on screen. */
interface UiWorkspace {
  openSession(target: string): void;
  startSession(workspaceId?: string): void;
}

/** One catalogue row, as far as this bridge reads it. */
interface CatalogueRow {
  readonly title?: unknown;
  readonly archived?: unknown;
  /** The native list facts Orik's components paint, all of them from the stored session. */
  readonly updatedAt?: unknown;
  readonly running?: unknown;
  readonly blank?: unknown;
  /** Lineage: a subagent child carries both, and a child is not a conversation. */
  readonly parentId?: unknown;
  readonly origin?: unknown;
  /** Local reference counts by source; `mainView` marks the session on screen. */
  readonly retainedBy?: Readonly<Record<string, number | undefined>>;
}

/** The session layer this plugin injects. */
interface Sessions {
  /** Borrow a session another owner already retains; undefined when nothing holds it. */
  binding?(id: string): { readonly eventSource: Observable<EventWindow> } | undefined;
  /** The catalogue: the host's own list of this tenant's sessions, read cold (it never resumes one). */
  list?: Observable<{
    readonly phase?: "pending" | "ready";
    readonly ids?: readonly string[];
    readonly byId?: Readonly<Record<string, CatalogueRow>>;
  }>;
}

/** One conversation as the host catalogues it. */
export interface OrikSessionSummary {
  readonly id: string;
  readonly title?: string;
  readonly archived: boolean;
  readonly updatedAt?: number;
  readonly running?: boolean;
  readonly blank?: boolean;
  /** The workspace account that carries this session, when a registry row accounts for it. */
  readonly workspaceId?: string;
}

/** One delivery receipt: the files a `present` call put into the conversation's durable storage. */
export interface OrikDelivery {
  readonly callId: string;
  readonly turn: number;
  readonly files: readonly { readonly url: string; readonly mediaType: string }[];
}

/** The bridge published for the app's bundle, which the kernel cannot import directly. */
export interface OrikDshBridge {
  readonly version: 2;
  /** The session on screen in the conversation panel, as `ui-workspace` holds it; undefined for none. */
  readonly mainSession: Observable<string | undefined>;
  /**
   * Put a conversation on screen, once the catalogue lists it (a session Orik just created reaches the
   * catalogue a moment after it exists).
   */
  openSession(conversationId: string): Promise<void>;
  /** `ui-workspace`'s New Session: the current conversation's workspace, else the most recent one. */
  startSession(): void;
  /**
   * The files a conversation on screen has presented, from the delivery receipts in its loaded event window.
   *
   * Read from the same window the panel renders, so a page of older history brings its deliveries with it.
   * Empty while nothing retains the session.
   */
  deliveries(conversationId: string): Observable<readonly OrikDelivery[]>;
  /** The host's own list of this tenant's sessions, for anything that enumerates them. */
  list(): OrikSessionSummary[];
  /** Both the session baseline and workspace membership/archive baseline have arrived. */
  catalogueReady(): boolean;
  /** Observe the native session and workspace stores; no HTTP refresh is needed. */
  subscribeCatalogue(listener: () => void): () => void;
}

/** The global the app's bundle reads; set when the kernel applies this plugin. */
const BRIDGE_GLOBAL = "__ORIK_DSH__";

/**
 * The plugin body: publish the bridge.
 *
 * @param ctx - the booted kernel: its session layer, workspace registry and `ui-workspace`'s navigation.
 */
export function apply(ctx: { sessions: Sessions; workspaces?: Workspaces; uiWorkspace?: UiWorkspace }): void {
  const navigation = (): UiWorkspace => {
    if (ctx.uiWorkspace === undefined) throw new Error("orik web: this client has no workspace navigation (ui-workspace)");
    return ctx.uiWorkspace;
  };
  const bridge: OrikDshBridge = {
    version: 2,
    mainSession: mainSessionOf(ctx.sessions),
    async openSession(conversationId: string): Promise<void> {
      await waitForCatalogue(ctx.sessions, conversationId);
      navigation().openSession(conversationId);
    },
    startSession(): void {
      navigation().startSession();
    },
    list(): OrikSessionSummary[] {
      return nativeSummaries(ctx.sessions.list?.getSnapshot(), ctx.workspaces?.list?.getSnapshot());
    },
    catalogueReady(): boolean {
      return ctx.sessions.list?.getSnapshot().phase === "ready"
        && (ctx.workspaces === undefined || ctx.workspaces.list?.getSnapshot().phase === "ready");
    },
    subscribeCatalogue(listener: () => void): () => void {
      const stopSessions = ctx.sessions.list?.subscribe(listener);
      const stopWorkspaces = ctx.workspaces?.list?.subscribe(listener);
      return () => { stopSessions?.(); stopWorkspaces?.(); };
    },
    deliveries(conversationId: string): Observable<readonly OrikDelivery[]> {
      return deliveriesOf(ctx.sessions, conversationId);
    },
  };
  (globalThis as Record<string, unknown>)[BRIDGE_GLOBAL] = bridge;
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CATALOGUE_BRIDGE_EVENT));
}

/**
 * The session `ui-workspace` holds on screen, read from the catalogue's reference counts.
 *
 * A switch retains the new session before releasing the previous one, so for a moment two rows are retained
 * under `mainView`; the one that was not on screen before is the new one.
 */
export function mainSessionOf(sessions: Pick<Sessions, "list">): Observable<string | undefined> {
  let snapshot: unknown;
  let current: string | undefined;
  return {
    getSnapshot(): string | undefined {
      const list = sessions.list?.getSnapshot();
      if (list === snapshot) return current;
      snapshot = list;
      const byId = list?.byId ?? {};
      const retained = Object.keys(byId).filter(id => (byId[id]?.retainedBy?.[MAIN_VIEW_SOURCE] ?? 0) > 0);
      current = retained.length <= 1 ? retained[0] : retained.find(id => id !== current) ?? current;
      return current;
    },
    subscribe(listener: () => void): () => void {
      return sessions.list?.subscribe(listener) ?? (() => {});
    },
  };
}

/**
 * One conversation's delivery receipts, from the window of whoever retains it.
 *
 * The binding appears once the session is retained and opened, which `ui-workspace` does on its own schedule,
 * so the subscription follows the catalogue and re-attaches to the window when the binding changes.
 */
function deliveriesOf(sessions: Sessions, conversationId: string): Observable<readonly OrikDelivery[]> {
  let windowSnapshot: EventWindow | undefined;
  let cached: readonly OrikDelivery[] = [];
  const source = (): Observable<EventWindow> | undefined => sessions.binding?.(conversationId)?.eventSource;
  return {
    getSnapshot(): readonly OrikDelivery[] {
      const current = source()?.getSnapshot();
      if (current === windowSnapshot) return cached;
      windowSnapshot = current;
      cached = (current?.entries ?? []).flatMap(entry => {
        if (entry.type !== "event") return [];
        const delivery = deliveryFrom(entry.event);
        return delivery === undefined ? [] : [{ callId: delivery.callId, turn: delivery.turn, files: delivery.files }];
      });
      return cached;
    },
    subscribe(listener: () => void): () => void {
      let attached: Observable<EventWindow> | undefined;
      let stopWindow: (() => void) | undefined;
      const attach = (): void => {
        const next = source();
        if (next === attached) return;
        stopWindow?.();
        attached = next;
        stopWindow = next?.subscribe(listener);
        listener();
      };
      const stopList = sessions.list?.subscribe(attach);
      attach();
      return () => { stopList?.(); stopWindow?.(); };
    },
  };
}

/** One catalogue entry, as the client's session list projects it. */
type CatalogueEntry = { readonly byId?: Readonly<Record<string, CatalogueRow>>; readonly ids?: readonly string[] };

/** The registry's workspace rows, each carrying the sessions it accounts for. */
type WorkspaceRows = { readonly items?: readonly {
  readonly workspaceId: string;
  readonly sessionIds?: readonly string[];
}[]; readonly archivedSessionIds?: readonly string[] };

/**
 * The conversations: dsh's own account of this deployment's sessions.
 *
 * Membership is the workspace registry's rows — the sessions a workspace accounts for, in the registry's manual
 * order. A subagent child is not a conversation — it carries both `origin: 'subagent'` and a `parentId`, and the
 * workspace account includes children (the controller attaches them too), so they are excluded.
 *
 * Exported for its own test: the ordering, the exclusions and the archived authority are the whole rule,
 * and they are cheaper to pin here than through a booted kernel.
 */
export function nativeSummaries(catalogue: CatalogueEntry | undefined, workspaces: WorkspaceRows | undefined): OrikSessionSummary[] {
  const byId = catalogue?.byId ?? {};
  const archived = new Set((workspaces?.archivedSessionIds ?? []).map(String));
  const accounted = (workspaces?.items ?? []).flatMap(workspace =>
    (workspace.sessionIds ?? []).map(id => ({ id: String(id), workspaceId: String(workspace.workspaceId) })));
  return accounted
    // Registry membership is not proof of a session's existence. Stale/unpersisted references must
    // not become phantom entries.
    .filter(entry => byId[entry.id] !== undefined && byId[entry.id]?.origin !== "subagent" && byId[entry.id]?.parentId === undefined)
    .map(entry => {
      return {
        id: entry.id,
        ...typeof byId[entry.id]?.title === "string" ? { title: byId[entry.id]?.title as string } : {},
        // The registry is the authority on what is archived; a catalogue flag is the fallback for a
        // composition that has no registry.
        archived: archived.has(entry.id) || byId[entry.id]?.archived === true,
        ...typeof byId[entry.id]?.updatedAt === "number" ? { updatedAt: byId[entry.id]?.updatedAt as number } : {},
        ...typeof byId[entry.id]?.running === "boolean" ? { running: byId[entry.id]?.running as boolean } : {},
        ...typeof byId[entry.id]?.blank === "boolean" ? { blank: byId[entry.id]?.blank as boolean } : {},
        ...entry.workspaceId === undefined ? {} : { workspaceId: entry.workspaceId },
      };
    });
}

/**
 * Wait until the catalogue lists a session, or its baseline is ready without it, bounded by a deadline.
 *
 * A client that has just booted has not been sent the host's session list yet, and a session Orik just created
 * reaches it a moment later; `ui-workspace` refuses to open a session the catalogue does not know.
 */
async function waitForCatalogue(sessions: Sessions, id: string, timeoutMs = CATALOGUE_WAIT_MS): Promise<void> {
  const list = sessions.list;
  if (list === undefined || catalogued(sessions, id)) return;
  await new Promise<void>(resolve => {
    let unsubscribe = (): void => {};
    const finish = (): void => {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    };
    const timer = setTimeout(finish, timeoutMs);
    unsubscribe = list.subscribe(() => { if (catalogued(sessions, id)) finish(); });
    // Close the subscribe/read race, including a synchronously delivered notification.
    if (catalogued(sessions, id)) finish();
  });
}

/** Whether the host's catalogue currently lists this session. */
function catalogued(sessions: Sessions, id: string): boolean {
  const snapshot = sessions.list?.getSnapshot();
  // No catalogue to consult: the open's own error is the only signal there is.
  if (snapshot?.ids === undefined) return true;
  return snapshot.ids.some(candidate => String(candidate) === id);
}
